import React, { useEffect, useRef, forwardRef, useContext, RefObject, InputHTMLAttributes } from 'react';

import { ReactComponent as CheckIcon } from '../../../icons/_check.svg';
import { ReactComponent as HyphenIcon } from '../../../icons/_hyphen.svg';

import { StyledHiddenCheckbox, StyledCheckbox, StyledCheck, StyledCheckboxButtonWrapper, StyledIndeterminateCheck } from './Checkbox.styles';
import FormColContext from '../_FormColContext';

export interface CheckboxProps extends InputHTMLAttributes<HTMLInputElement> {
  /** property to display the checkbox in a indeterminate state */
  indeterminate?: boolean;
  /** Color of the unselect checkbox. */
  color?: string;
  /** Do you apply the focus style. */
  noFocus?: boolean;
  /** @ignore */
  readOnly?: boolean;
  /** @ignore */
  disabled?: boolean;
  /** @ignore */
  textOnly?: boolean;
  /** @ignore */
  checked?: boolean;
}

export interface CheckboxStatics {
  StyledCheckbox: typeof StyledCheckbox;
}

/**
 * @mode developer
 */
const Checkbox = Object.assign(
  forwardRef<HTMLInputElement, CheckboxProps>(({
    readOnly, color, noFocus = false, disabled, textOnly, indeterminate, className, ...props
  }, ref) => {
    const { colId } = useContext(FormColContext);
    const checkBoxRef = useRef<HTMLInputElement>();
    const resolveRef = (ref || checkBoxRef) as RefObject<HTMLInputElement>;

    useEffect(() => {
      resolveRef.current.indeterminate = indeterminate;
    }, [indeterminate, resolveRef]);

    return (
      <>
        <StyledHiddenCheckbox
          type="checkbox"
          data-testid="Checkbox"
          disabled={disabled || readOnly || textOnly}
          ref={resolveRef}
          aria-labelledby={colId}
          {...props}
        />
        <StyledCheckboxButtonWrapper
          className={`${className} ${!noFocus ? 'ia-react-core__checkbox--focus' : ''}`}
        >
          <StyledCheckbox
            role="presentation"
            focusable="false"
            color={color}
          >
            <StyledCheck as={CheckIcon} />
            <StyledIndeterminateCheck as={HyphenIcon} />

          </StyledCheckbox>
        </StyledCheckboxButtonWrapper>
      </>
    );
  }),
  {
    displayName: 'Checkbox',
    StyledCheckbox,
    StyledCheckboxButtonWrapper,
  },
);

export default Checkbox;
